import dayjs, {Dayjs} from 'dayjs';

import {LocaleContextType, LocaleNameEnum} from '../../../provider/locale/locale-context-type';
import {LangKeyType} from '../../../provider/locale/translation/type';
import {ReviewsAnalysisModeEnum} from '../../../service/reivew-analysis/reivew-analysis-const';
import {ReviewRateEnum} from '../../../service/reivew-analysis/review-rate/review-rate-type';
import {SentimentEnum} from '../../../service/reivew-analysis/review-sentiment/review-sentiment-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {isDateValid} from '../../../util/date';
import {getEnumValue, getEnumValueEnsure} from '../../../util/enum';
import {NeverError} from '../../../util/error';
import {getFormattedDateTime} from '../../../util/format';
import {QueryMapType} from '../../../util/type';

import {KeywordsAndTopicsFormTypeAggregationEnum} from './pages/keywords-and-topics-page/keywords-and-topics-form/keywords-and-topics-type';
import {defaultReviewsAnalysisDaysPeriod} from './reviews-analysis-const';
import {
    ComparePeriodFilterRequestType,
    CompetitorFilterRequestType,
    ReviewsAnalysisFilterEnum,
    ReviewsAnalysisFilterType,
    ReviewsCompareEnum,
    SingleFilterRequestType,
} from './reviews-analysis-type';
import * as styles from './reviews-analysis-palette-export.scss';

export function getCompareModeState(filterState: ReviewsAnalysisFilterType): ReviewsCompareEnum {
    if (filterState[ReviewsAnalysisFilterEnum.CompareRange].some(Boolean)) {
        return ReviewsCompareEnum.PERIOD;
    }

    if (filterState[ReviewsAnalysisFilterEnum.Competitor]) {
        return ReviewsCompareEnum.COMPETITOR;
    }

    return ReviewsCompareEnum.OFF;
}

export function getSentimentStringKeyBySentiment(sentiment: SentimentEnum): LangKeyType {
    switch (sentiment) {
        case SentimentEnum.Negative:
            return 'REVIEWS_ANALYSIS__SENTIMENT__NEGATIVE';
        case SentimentEnum.Positive:
            return 'REVIEWS_ANALYSIS__SENTIMENT__POSITIVE';
        case SentimentEnum.Neutral:
            return 'REVIEWS_ANALYSIS__SENTIMENT__NEUTRAL';
        case SentimentEnum.Unspecified:
            return 'REVIEWS_ANALYSIS__SENTIMENT__UNSPECIFIED';
        default:
            throw new NeverError(sentiment);
    }
}

export function getSentimentColorBySentiment(sentiment: SentimentEnum): string {
    switch (sentiment) {
        case SentimentEnum.Negative:
            return styles.sentiment_negative;
        case SentimentEnum.Positive:
            return styles.sentiment_positive;
        case SentimentEnum.Neutral:
            return styles.sentiment_neutral;
        case SentimentEnum.Unspecified:
            return styles.sentiment_unspecified;
        default:
            throw new NeverError(sentiment);
    }
}

export function normalizeReviewAnalysisDateRange(start: Date, end: Date): [Date, Date] {
    return [dayjs.utc(start).startOf('day').toDate(), dayjs.utc(end).endOf('day').toDate()];
}

export function getPeriodOfTimeFromUrl(value: string): [Date | null, Date | null] {
    const [start, end] = value.split(',');

    if (!start || !end) {
        return [null, null];
    }

    const fromDateUtc: Date = new Date(start);
    const toDateUtc: Date = new Date(end);

    if (!isDateValid(fromDateUtc) || !isDateValid(toDateUtc)) {
        return [null, null];
    }

    return normalizeReviewAnalysisDateRange(fromDateUtc, toDateUtc);
}

function getBooleanFromUrl(value: string): boolean | null {
    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return null;
}

export function serializeReviewAnalysisDate(date: Date): string | null {
    const dayjsDate = dayjs(date);

    return dayjsDate.subtract(dayjsDate.utcOffset(), 'minutes').format('YYYY-MM-DDTHH:mm:ss.SSSSSSZZ');
}

function getCatalogsFromUrl(value: string): Array<number> {
    return value
        .split(',')
        .map(Number)
        .filter<number>((id: number): id is number => Boolean(id) && !Number.isNaN(id));
}

export function getAggregationFromUrl(value?: string | void): KeywordsAndTopicsFormTypeAggregationEnum {
    return getEnumValueEnsure(
        KeywordsAndTopicsFormTypeAggregationEnum,
        value || '',
        KeywordsAndTopicsFormTypeAggregationEnum.Day
    );
}

export function getRangeFilterTagFormat(localeName: LocaleNameEnum, range: [Date | null, Date | null]): string {
    const [startDate, endDate] = range;

    if (!startDate || !endDate) {
        return '';
    }

    const dateFormatOptions = {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    } as const;

    const startFormat: string = getFormattedDateTime(
        localeName,
        dayjs.utc(startDate).startOf('day').toDate(),
        dateFormatOptions
    );
    const endFormat: string = getFormattedDateTime(
        localeName,
        dayjs.utc(endDate).startOf('day').toDate(),
        dateFormatOptions
    );

    return `${startFormat} - ${endFormat}`;
}

export function getComparePeriodTitle(
    localeName: LocaleNameEnum,
    range: [Date | null, Date | null],
    getLocalizedString: LocaleContextType['getLocalizedString']
): string {
    return (
        getRangeFilterTagFormat(localeName, range) ||
        getLocalizedString('REVIEWS_ANALYSIS__COMPARE_PERIOD_TITLE__DEFAULT', {
            daysCount: defaultReviewsAnalysisDaysPeriod.toString(),
        })
    );
}

// eslint-disable-next-line complexity, max-statements, sonarjs/cognitive-complexity
export function getFilterStateFromUrlQuery(
    queries: QueryMapType<ReviewsAnalysisFilterEnum>
): ReviewsAnalysisFilterType {
    const rangeQuery: string = queries[ReviewsAnalysisFilterEnum.Range] || '';
    const range: [Date | null, Date | null] = getPeriodOfTimeFromUrl(rangeQuery);

    const compareRangeQuery: string = queries[ReviewsAnalysisFilterEnum.CompareRange] || '';
    const compareRange: [Date | null, Date | null] = getPeriodOfTimeFromUrl(compareRangeQuery);

    const competitorIdQuery = String(queries[ReviewsAnalysisFilterEnum.Competitor]);
    const competitorId: number | null = Number.parseInt(competitorIdQuery, 10) || null;

    const competitorCompanyIdQuery = String(queries[ReviewsAnalysisFilterEnum.CompetitorCompany]);
    const competitorCompanyId: number | null = Number.parseInt(competitorCompanyIdQuery, 10) || null;

    const sentimentsQuery: string = queries[ReviewsAnalysisFilterEnum.Sentiments] || '';
    const sentiments: Array<SentimentEnum> = sentimentsQuery
        .split(',')
        .map((value: string): SentimentEnum | null => getEnumValue<SentimentEnum>(SentimentEnum, value))
        .filter(Boolean);

    const catalogsQuery: string = queries[ReviewsAnalysisFilterEnum.Catalogs] || '';
    const catalogs: Array<number> = getCatalogsFromUrl(catalogsQuery);

    const tagsQuery: string = queries[ReviewsAnalysisFilterEnum.Tags] || '';
    const tags: Array<string> = tagsQuery.split(',').filter(Boolean);

    const phrasesQuery: string = queries[ReviewsAnalysisFilterEnum.Phrases] || '';
    const phrases: Array<string> = phrasesQuery.split(',').filter(Boolean);

    const topicsQuery: string = queries[ReviewsAnalysisFilterEnum.Topics] || '';
    const topics: Array<string> = topicsQuery.split(',').filter(Boolean);

    const userTagsIdsQuery: string = queries[ReviewsAnalysisFilterEnum.UserTagsIds] || '';
    const userTagsIds: Array<number> = getCatalogsFromUrl(userTagsIdsQuery);

    const aspectIdsQuery: string = queries[ReviewsAnalysisFilterEnum.AspectIds] || '';
    const aspectIds: Array<number> = getCatalogsFromUrl(aspectIdsQuery);

    const ratesQuery: string = queries[ReviewsAnalysisFilterEnum.Rates] || '';
    const rates: Array<ReviewRateEnum> = ratesQuery
        .split(',')
        .map((value: string): ReviewRateEnum | null => getEnumValue<ReviewRateEnum>(ReviewRateEnum, value))
        .filter(Boolean);

    const withMessageQuery: string = queries[ReviewsAnalysisFilterEnum.WithMessage] || '';
    const withMessage: boolean | null = getBooleanFromUrl(withMessageQuery);

    const withReplyQuery: string = queries[ReviewsAnalysisFilterEnum.WithReply] || '';
    const withReply: boolean | null = getBooleanFromUrl(withReplyQuery);

    const ableToReplyQuery: string = queries[ReviewsAnalysisFilterEnum.AbleToReply] || '';
    const ableToReply: boolean | null = getBooleanFromUrl(ableToReplyQuery);

    const reviewTypeQuery: string = queries[ReviewsAnalysisFilterEnum.Target] || '';

    const useDeletedQuery: string = queries[ReviewsAnalysisFilterEnum.UseDeleted] || '';
    const useDeleted: boolean | null = getBooleanFromUrl(useDeletedQuery);

    const timeFormatQuery: string = queries[ReviewsAnalysisFilterEnum.TimeFormat] || '';

    const filterState: ReviewsAnalysisFilterType = {
        [ReviewsAnalysisFilterEnum.Range]: range,
        [ReviewsAnalysisFilterEnum.CompareRange]: compareRange,
        [ReviewsAnalysisFilterEnum.Competitor]: competitorId,
        [ReviewsAnalysisFilterEnum.CompetitorCompany]: competitorCompanyId,
        [ReviewsAnalysisFilterEnum.Sentiments]: sentiments,
        [ReviewsAnalysisFilterEnum.Catalogs]: catalogs,
        [ReviewsAnalysisFilterEnum.Tags]: tags,
        [ReviewsAnalysisFilterEnum.Phrases]: phrases,
        [ReviewsAnalysisFilterEnum.Topics]: topics,
        [ReviewsAnalysisFilterEnum.Rates]: rates,
        [ReviewsAnalysisFilterEnum.WithMessage]: withMessage,
        [ReviewsAnalysisFilterEnum.WithReply]: withReply,
        [ReviewsAnalysisFilterEnum.AbleToReply]: ableToReply,
        [ReviewsAnalysisFilterEnum.Target]: reviewTypeQuery,
        [ReviewsAnalysisFilterEnum.UseDeleted]: useDeleted,
        [ReviewsAnalysisFilterEnum.UserTagsIds]: userTagsIds,
        [ReviewsAnalysisFilterEnum.AspectIds]: aspectIds,
        [ReviewsAnalysisFilterEnum.TimeFormat]: getEnumValueEnsure(
            ReviewsAndAnswersStatisticsUnitMeasurementEnum,
            timeFormatQuery,
            null
        ),
    };

    const aggregation = getAggregationFromUrl(queries[ReviewsAnalysisFilterEnum.Aggregation]);

    if (aggregation !== null) {
        filterState[ReviewsAnalysisFilterEnum.Aggregation] = aggregation;
    }

    return filterState;
}

export function serializeSingleDrawerFilters(
    filter: ReviewsAnalysisFilterType,
    isTopicsAllowed?: boolean,
    defaultDateRange?: [Date, Date]
): SingleFilterRequestType {
    const [startDateFromRange, endDateFromRange] = filter[ReviewsAnalysisFilterEnum.Range];

    const startDate = startDateFromRange || defaultDateRange?.[0];
    const endDate = endDateFromRange || defaultDateRange?.[1];

    return {
        startDate: startDate ? serializeReviewAnalysisDate(startDate) : null,
        endDate: endDate ? serializeReviewAnalysisDate(endDate) : null,
        sentiments: filter[ReviewsAnalysisFilterEnum.Sentiments],
        sources: filter[ReviewsAnalysisFilterEnum.Catalogs],
        tags: filter[ReviewsAnalysisFilterEnum.Tags],
        phrases: filter[ReviewsAnalysisFilterEnum.Phrases],
        topics: isTopicsAllowed ? filter[ReviewsAnalysisFilterEnum.Topics] : [],
        rating: filter[ReviewsAnalysisFilterEnum.Rates],
        withAnswers: filter[ReviewsAnalysisFilterEnum.WithReply],
        withText: filter[ReviewsAnalysisFilterEnum.WithMessage],
        ableToReply: filter[ReviewsAnalysisFilterEnum.AbleToReply],
        target: filter[ReviewsAnalysisFilterEnum.Target],
        useDeleted: filter[ReviewsAnalysisFilterEnum.UseDeleted],
        userTagsIds: filter[ReviewsAnalysisFilterEnum.UserTagsIds],
        aspectIds: filter[ReviewsAnalysisFilterEnum.AspectIds],
        timeFormat: filter[ReviewsAnalysisFilterEnum.TimeFormat],
        // groupBy: filter[ReviewsAnalysisFilterEnum.Aggregation],
    };
}

export function serializePeriodDrawerFilters(filter: ReviewsAnalysisFilterType): ComparePeriodFilterRequestType {
    const [startDate, endDate] = filter[ReviewsAnalysisFilterEnum.CompareRange];

    return {
        ...serializeSingleDrawerFilters(filter),
        startDate: startDate ? serializeReviewAnalysisDate(startDate) : null,
        endDate: endDate ? serializeReviewAnalysisDate(endDate) : null,
    };
}

export function serializeCompetitorDrawerFilters(
    filter: ReviewsAnalysisFilterType,
    defaultDateRange?: [Date, Date]
): CompetitorFilterRequestType {
    const competitorId: number | null = filter[ReviewsAnalysisFilterEnum.Competitor];
    const competitorCompanyId: number | null = filter[ReviewsAnalysisFilterEnum.CompetitorCompany];

    return {
        ...serializeSingleDrawerFilters(filter, false, defaultDateRange),
        mode: ReviewsAnalysisModeEnum.competitors,
        brandIds: competitorId ? [competitorId] : [],
        companyIds: competitorCompanyId ? [competitorCompanyId] : [],
    };
}

export function rangeDisabledDate(date: Dayjs): boolean {
    const yesterday = dayjs().subtract(2, 'day');

    return date.isSameOrAfter(yesterday);
}
